import { useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Check, X, Trash2, Edit3, Save, Filter, UserCheck, Users, RefreshCw } from 'lucide-react';

const AdminRegistrationsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [genderFilter, setGenderFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/registrations', { params: { status: statusFilter, gender: genderFilter } });
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'تعذّر التحميل');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, genderFilter]);

  const approve = async (id) => {
    if (!window.confirm('اعتماد هذا التسجيل وإنشاء رقم قرائي جديد؟')) return;
    try {
      const res = await api.post(`/admin/registrations/${id}/approve`);
      toast.success(`تم الاعتماد. الرقم القرائي: ${res.data.reader_number}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'فشل الاعتماد'); }
  };

  const reject = async (id) => {
    if (!window.confirm('رفض هذا الطلب؟')) return;
    try { await api.post(`/admin/registrations/${id}/reject`); toast.success('تم الرفض'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'فشل'); }
  };

  const remove = async (id) => {
    if (!window.confirm('حذف نهائي لهذا الطلب؟')) return;
    try { await api.delete(`/admin/registrations/${id}`); toast.success('تم الحذف'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'فشل'); }
  };

  const startEdit = (row) => { setEditingId(row.id); setEditData({ ...row.data }); };
  const saveEdit = async () => {
    try { await api.put(`/admin/registrations/${editingId}`, editData); toast.success('تم الحفظ'); setEditingId(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'فشل'); }
  };

  return (
    <div dir="rtl" className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-registrations-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <UserCheck className="w-7 h-7" style={{color:'#C19A6B'}} />
          <h1 className="text-2xl font-bold" style={{color:'#5D4037'}}>طلبات التسجيل في المبادرة</h1>
        </div>
        <button onClick={load} className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2 text-sm" data-testid="reg-refresh-btn">
          <RefreshCw className="w-4 h-4" />تحديث
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-gray-500" />
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">الحالة:</span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="status-filter">
            <option value="pending">قيد المراجعة</option>
            <option value="approved">مُعتمد</option>
            <option value="rejected">مرفوض</option>
            <option value="all">الكل</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">الجنس:</span>
          <select value={genderFilter} onChange={e => setGenderFilter(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="gender-filter">
            <option value="all">الكل</option>
            <option value="male">ذكور</option>
            <option value="female">إناث</option>
          </select>
        </div>
        <div className="text-xs text-gray-500 mr-auto">العدد: <b>{rows.length}</b></div>
      </div>

      {loading ? <div className="text-center py-10 text-gray-500">جارٍ التحميل...</div> : (
        rows.length === 0 ? <div className="text-center py-10 text-gray-400">لا يوجد طلبات</div> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {rows.map(r => {
              const isEditing = editingId === r.id;
              const d = r.data || {};
              return (
                <div key={r.id} className="bg-white rounded-xl shadow border p-4" data-testid={`reg-card-${r.id}`}>
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div>
                      <h3 className="font-bold text-lg" style={{color:'#5D4037'}}>{d.full_name || 'بدون اسم'}</h3>
                      <p className="text-xs text-gray-500">{new Date(r.created_at).toLocaleString('ar')}</p>
                    </div>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {r.status === 'approved' ? `معتمد (${r.reader_number})` : r.status === 'rejected' ? 'مرفوض' : 'قيد المراجعة'}
                    </span>
                  </div>

                  {!isEditing ? (
                    <dl className="text-sm space-y-1.5 text-gray-700">
                      <Row label="الجنس" v={d.gender} />
                      <Row label="المحافظة" v={d.governorate} />
                      <Row label="الولاية" v={d.state} />
                      <Row label="البريد" v={d.email} />
                      <Row label="الهاتف (واتساب)" v={d.phone} mono />
                      <Row label="إنستجرام" v={d.instagram} />
                      <Row label="التحدّي" v={d.challenge} />
                    </dl>
                  ) : (
                    <div className="space-y-2 text-sm">
                      {['full_name','gender','governorate','state','email','phone','instagram','challenge'].map(k => (
                        <div key={k}>
                          <label className="block text-xs text-gray-500 mb-0.5">{k}</label>
                          <input value={editData[k] || ''} onChange={e => setEditData(p => ({...p, [k]: e.target.value}))} className="w-full border rounded px-2 py-1 text-sm" />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t">
                    {r.status === 'pending' && !isEditing && (
                      <>
                        <button onClick={() => approve(r.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700" data-testid={`approve-${r.id}`}>
                          <Check className="w-4 h-4" />اعتماد
                        </button>
                        <button onClick={() => reject(r.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-sm hover:bg-red-200" data-testid={`reject-${r.id}`}>
                          <X className="w-4 h-4" />رفض
                        </button>
                      </>
                    )}
                    {!isEditing ? (
                      <button onClick={() => startEdit(r)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200" data-testid={`edit-${r.id}`}>
                        <Edit3 className="w-4 h-4" />تعديل
                      </button>
                    ) : (
                      <>
                        <button onClick={saveEdit} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700">
                          <Save className="w-4 h-4" />حفظ
                        </button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm">إلغاء</button>
                      </>
                    )}
                    <button onClick={() => remove(r.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-sm hover:bg-red-100 mr-auto" data-testid={`delete-${r.id}`}>
                      <Trash2 className="w-4 h-4" />حذف
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

const Row = ({ label, v, mono }) => (
  <div className="flex gap-2"><dt className="text-gray-500 min-w-[110px]">{label}:</dt><dd className={mono ? 'font-mono' : ''}>{v || <span className="text-gray-300">—</span>}</dd></div>
);

export default AdminRegistrationsPage;
